import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  FaToolbox,
  FaTractor,
  FaShieldAlt,
  FaLeaf,
  FaTruck,
  FaProjectDiagram,
} from "react-icons/fa";
import "../Services/Services.css";

// Services List Array (Basic info)
const servicesList = [
  { id: 1, name: "Exploration and Surveying", icon: <FaToolbox /> },
  { id: 2, name: "Resource Extraction", icon: <FaTractor /> },
  { id: 3, name: "Mining Safety & Risk Management", icon: <FaShieldAlt /> },
  { id: 4, name: "Environmental Management", icon: <FaLeaf /> },
  { id: 5, name: "Logistics and Transportation", icon: <FaTruck /> },
  {
    id: 6,
    name: "Consulting and Project Management",
    icon: <FaProjectDiagram />,
  },
];

// Service Details Array (detailed info for each service)
const servicesDetails = [
  {
    id: 1,
    name: "Exploration and Surveying",
    subname: "Precision in Exploration",
    quote: "“Accurate surveys today, reliable mining tomorrow.”",
    description:
      "Detailed exploration and surveying services for mineral resources, ensuring precise data for mining operations.",
    mission:
      "To provide accurate, innovative, and safe surveying techniques that drive the mining industry forward.",
    vision:
      "To be a leader in exploration services, delivering unmatched expertise and precision in every project.",
    posterImage: "/s1.webp", // Poster image added here
    recruitmentSteps: [
      {
        step: 1,
        title: "Application Submission",
        description:
          "Submit your resume and relevant qualifications, including geological survey experience.",
        image: "/as.webp",
      },
      {
        step: 2,
        title: "Pre-Screening and Qualification Check",
        description:
          "Our team verifies your qualifications and checks for relevant certifications in exploration.",
        image: "/ae.webp",
      },
      {
        step: 3,
        title: "Technical Interview",
        description:
          "Discuss your knowledge of exploration tools, survey equipment, and techniques used in the field.",
        image: "/ti.webp",
      },
      {
        step: 4,
        title: "Safety Induction",
        description:
          "Complete necessary safety training and site-specific inductions for working in the field.",
        image: "/si.webp",
      },
    ],
  },
  {
    id: 2,
    name: "Resource Extraction",
    subname: "Efficient Extraction Processes",
    quote: "“Maximizing resources with minimal impact.”",
    description:
      "Efficient and safe extraction of natural resources, maximizing productivity while minimizing environmental impact.",
    mission:
      "To optimize resource extraction processes using cutting-edge technology and practices for increased productivity.",
    vision:
      "To lead the industry in sustainable and efficient extraction methods that minimize environmental impact.",
    posterImage: "/s2.webp", // Poster image added here
    recruitmentSteps: [
      {
        step: 1,
        title: "Submit Your Application",
        description:
          "Apply with your credentials, including prior experience in extraction operations or relevant mining roles.",
        image: "/as.webp",
      },
      {
        step: 2,
        title: "Experience Review",
        description:
          "Our team reviews your past experience in extraction, equipment handling, and safety practices.",
        image: "/er.webp",
      },
      {
        step: 3,
        title: "Skills Test and Assessment",
        description:
          "Undergo a skills test to demonstrate your proficiency with mining equipment, excavation, and extraction techniques.",
        image: "/st.webp",
      },
      {
        step: 4,
        title: "Field Training and Safety Certification",
        description:
          "Complete field training and acquire necessary safety certifications before joining extraction operations.",
        image: "/fs.webp",
      },
    ],
  },
  {
    id: 3,
    name: "Mining Safety & Risk Management",
    subname: "Safety Above All",
    quote: "“Protecting lives with every step.”",
    description:
      "Safety protocols and risk management for mining operations to ensure worker and environmental safety.",
    mission:
      "To create a safe and secure working environment for all employees and stakeholders in the mining industry.",
    vision:
      "To set the benchmark for mining safety and risk management through continuous innovation and dedication.",
    posterImage: "/s3.webp", // Poster image added here
    recruitmentSteps: [
      {
        step: 1,
        title: "Safety and Risk Management Application",
        description:
          "Submit your application, focusing on your expertise in mining safety and risk mitigation practices.",
        image: "/as.webp",
      },
      {
        step: 2,
        title: "Qualification Check and Screening",
        description:
          "Our team reviews your certifications, safety record, and understanding of risk management protocols in mining.",
        image: "/er.webp",
      },
      {
        step: 3,
        title: "Safety Interview",
        description:
          "Demonstrate your understanding of mining safety regulations, emergency response procedures, and hazard assessment methods.",
        image: "/ti.webp",
      },
      {
        step: 4,
        title: "Final Safety Training",
        description:
          "Undergo comprehensive safety training specific to mining environments before working on-site.",
        image: "/si.webp",
      },
    ],
  },
  {
    id: 4,
    name: "Environmental Management",
    subname: "Sustainable Practices",
    quote: "“Mining with care for the planet.”",
    description:
      "Sustainable practices for managing the environmental impact of projects, from mining to waste management.",
    mission:
      "To provide environmentally-friendly solutions that reduce the ecological footprint of mining operations while ensuring compliance with all regulations.",
    vision:
      "To be recognized as the top provider of sustainable environmental management solutions in the mining industry.",
    posterImage: "/s4.webp", // Poster image added here
    recruitmentSteps: [
      {
        step: 1,
        title: "Application for Environmental Roles",
        description:
          "Submit your application, highlighting experience in environmental management and sustainability practices in mining.",
        image: "/as.webp",
      },
      {
        step: 2,
        title: "Environmental Experience Screening",
        description:
          "We assess your experience with environmental assessments, waste management, and environmental regulations in mining.",
        image: "/er.webp",
      },
      {
        step: 3,
        title: "Sustainability and Compliance Interview",
        description:
          "Discuss your practical experience with environmental management systems, sustainability strategies, and regulatory compliance.",
        image: "/ti.webp",
      },
      {
        step: 4,
        title: "Training in Environmental Protocols",
        description:
          "Complete required training on environmental protocols and sustainability practices for mining operations.",
        image: "/et.webp",
      },
    ],
  },
  {
    id: 5,
    name: "Logistics and Transportation",
    subname: "Efficient Logistics Solutions",
    quote: "“Seamless delivery, reliable service.”",
    description:
      "Efficient logistics and transportation services for mining and extraction, ensuring the safe and timely delivery of materials.",
    mission:
      "To streamline the transportation of resources with advanced logistics solutions that minimize delays and maximize efficiency.",
    vision:
      "To revolutionize transportation and logistics in the mining industry through cutting-edge technology and sustainability.",
    posterImage: "/s5.webp", // Poster image added here
    recruitmentSteps: [
      {
        step: 1,
        title: "Logistics Role Application",
        description:
          "Submit your application with details about your logistics and transportation experience in the mining sector.",
        image: "/as.webp",
      },
      {
        step: 2,
        title: "Logistics Experience Screening",
        description:
          "We review your experience with supply chain management, transport coordination, and inventory systems.",
        image: "/er.webp",
      },
      {
        step: 3,
        title: "Operational Skills Test",
        description:
          "Complete a test to demonstrate your knowledge of logistical planning, resource transportation, and safety standards in mining.",
        image: "/st.webp",
      },
      {
        step: 4,
        title: "Onboarding and Safety Training",
        description:
          "Receive training on equipment safety, route planning, and regulatory guidelines for transporting mining resources.",
        image: "/si.webp",
      },
    ],
  },
  {
    id: 6,
    name: "Consulting and Project Management",
    subname: "Expert Project Solutions",
    quote: "“Bringing projects to life with expert precision.”",
    description:
      "Expert consulting and project management for mining projects, ensuring the seamless execution of operations from start to finish.",
    mission:
      "To deliver expert advice and management services that ensure mining projects are completed on time, within budget, and with full regulatory compliance.",
    vision:
      "To become the go-to consulting firm for the mining industry, known for our reliability and deep expertise in project management.",
    posterImage: "/s6.webp", // Poster image added here
    recruitmentSteps: [
      {
        step: 1,
        title: "Project Management Application",
        description:
          "Submit your application, highlighting experience in managing large-scale mining projects, including budgeting and scheduling.",
        image: "/as.webp",
      },
      {
        step: 2,
        title: "Project Experience Screening",
        description:
          "We assess your prior experience in overseeing mining projects, from feasibility to execution.",
        image: "/er.webp",
      },
      {
        step: 3,
        title: "Project Management Interview",
        description:
          "Discuss your project management approach, particularly in resource allocation, risk management, and team leadership in mining.",
        image: "/ti.webp",
      },
      {
        step: 4,
        title: "Project Induction and Onboarding",
        description:
          "Complete induction training specific to current mining projects, including safety and project-specific requirements.",
        image: "/on.webp",
      },
    ],
  },
];
const Services = () => {
  const { serviceId } = useParams(); // Fetch dynamic service ID from URL
  const selectedService =
    servicesDetails.find((service) => service.id === parseInt(serviceId)) ||
    servicesDetails[0]; // Find selected service or default to the first one

  return (
    <div className="services">
      {/* Display the selected service name and subname */}
      <h1 className="services-title">{selectedService.name}</h1>
      <hr className="red-line" />

      {/* Display poster image */}
      <div className="services-poster">
        <img
          src={selectedService.posterImage}
          alt="Poster"
          className="poster-image"
        />
        <div className="poster-overlay">
          <h2 className="poster-title">{selectedService.name}</h2>
          <p className="poster-subtitle">{selectedService.subname}</p>
        </div>
      </div>

      <div className="services-content">
        <div className="services-grid">
          {/* Left Sidebar */}
          <div className="services-sidebar">
            <h3>Service List</h3>
            <ul>
              {servicesList.map((service) => (
                <li key={service.id}>
                  {service.icon}{" "}
                  <Link to={`/service/${service.id}`}>{service.name}</Link>
                </li>
              ))}
            </ul>

            {/* Service Quote and Call to Action */}
            <div className="service-quote">
              <blockquote>{selectedService.quote}</blockquote>
              <h3 className="contact-header">Get in Touch with Us</h3>
              <button className="contact-btn">Contact Now</button>
            </div>
          </div>

          {/* Right Content */}
          <div className="services-details">
            <h3>What We Do</h3>
            <p>{selectedService.description}</p>
            <div className="mv">
              <div className="m">
                <h3>Our Mission</h3>
                <p>{selectedService.mission}</p>
              </div>
              <div className="v">
                <h3>Our Vision</h3>
                <p>{selectedService.vision}</p>
              </div>
            </div>

            <h3>Recruitment Process</h3>
            <p className="recruitment-summary">
              Our recruitment process ensures that we select the most qualified
              and dedicated individuals to join our team. Follow the steps to
              become a part of our journey!
            </p>

            <div className="recruitment-process">
              {selectedService.recruitmentSteps.map((step) => (
                <div key={step.step} className="process-step">
                  <div className="step-bar">
                    <div className="step-number">Step: {step.step}</div>
                    <div className="step-title">{step.title}</div>
                  </div>
                  <div className="step-content">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="step-image"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;
